import { useState } from "react";
import { ArrowRight } from "lucide-react";

const TOTAL_SPOTS = 500;

export default function HeroSection({ onSubmit, loading, submitted, message, waitlistCount, submittedRank }) {
  const [email, setEmail] = useState("");
  const [localError, setLocalError] = useState("");

  const handleSubmit = (e) => {
    e.preventDefault();
    setLocalError("");
    const trimmed = email.trim();
    if (!trimmed) {
      setLocalError("Please enter a valid email address.");
      return;
    }
    onSubmit(trimmed);
  };

  const claimed = waitlistCount || 0;
  const remaining = TOTAL_SPOTS - claimed;
  const progressPct = (claimed / TOTAL_SPOTS) * 100;

  return (
    <section
      id="hero"
      className="nm-section-light pt-[72px] md:pt-[90px] overflow-hidden md:min-h-screen flex flex-col"
      data-testid="hero-section"
    >
      <div className="nm-container flex-1 flex flex-col justify-center pb-6 pt-2 md:pb-14 md:pt-8">
        {/* ── Full-width Centered Block ── */}
        <div className="flex flex-col items-center text-center w-full max-w-[1024px] mx-auto px-2 sm:px-0">
          <h1
            className="text-[clamp(2.1rem,5.5vw,4.6rem)] leading-[1.05] md:leading-[0.95] text-[#111] tracking-[-0.02em] w-full"
            data-testid="hero-headline"
          >
            The AI Fitness Coach That Keeps You{" "}
            <span className="text-white bg-[#111] px-2 md:px-4 py-1 -ml-1 md:-ml-2 inline-block mt-1 mb-1 md:mb-0 whitespace-nowrap">
              Accountable
            </span>{" "}
            <span className="whitespace-nowrap">Every Day.</span>
          </h1>

          <p className="mt-4 md:mt-8 text-[15px] md:text-lg text-[#333] max-w-3xl leading-relaxed">
            <span className="font-['Space_Mono'] font-bold tracking-[0.05em]">EDITH</span> tracks your workouts, follows up when you miss them, and
            adapts your plan in real time so you stay on track, even when life gets in the way.
          </p>

          <p className="mt-3 text-[15px] md:text-lg font-bold text-[#111] tracking-tight">
            The Last Fitness App You'll Ever Need To Restart.
          </p>

          {/* ── Product Hunt Badge ── */}
          <a
            href="https://www.producthunt.com/posts/edith-e5bae397-95d3-44d6-9f75-855385d23ad6?utm_source=badge-featured&utm_medium=badge&utm_souce=badge-edith"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="hero-producthunt-badge"
            className="group relative mt-4 inline-flex items-center gap-3 bg-white border border-black/[0.08] rounded-[14px] pl-2 pr-4 py-2 shadow-[0_1px_2px_rgba(0,0,0,0.03)] hover:shadow-[0_8px_24px_-6px_rgba(218,85,47,0.25)] hover:border-[#DA552F]/25 hover:-translate-y-0.5 transition-all duration-300 ease-out"
          >
            {/* Logo tile */}
            <span className="relative flex items-center justify-center w-9 h-9 rounded-[10px] bg-gradient-to-br from-[#EA6C47] to-[#DA552F] shadow-[0_2px_6px_rgba(218,85,47,0.3)]">
              <svg width="20" height="20" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.667 20H17v-6h5.667a3 3 0 010 6m0-10H13v20h4v-6h5.667a7 7 0 100-14" fill="#FFF"/>
              </svg>
            </span>

            {/* Text block */}
            <span className="flex flex-col text-left leading-none">
              <span className="text-[10px] font-['Space_Mono'] font-bold uppercase tracking-[0.14em] text-black/45">
                Featured on
              </span>
              <span className="mt-1 text-[15px] font-bold text-[#111] tracking-[-0.01em]">
                Product Hunt
              </span>
            </span>

            {/* Divider */}
            <span className="h-8 w-px bg-black/10 mx-0.5" />

            {/* Upvote */}
            <span className="flex flex-col items-center justify-center">
              <svg width="14" height="10" viewBox="0 0 14 10" fill="none" xmlns="http://www.w3.org/2000/svg" className="group-hover:-translate-y-0.5 transition-transform duration-300 ease-out">
                <path d="M7 0L13.062 9H0.938L7 0Z" fill="#DA552F"/>
              </svg>
              <span className="mt-1 text-[10px] font-['Space_Mono'] font-bold uppercase tracking-[0.14em] text-[#DA552F]">
                Upvote
              </span>
            </span>
          </a>

          {/* ── Email CTA card ── */}
          <div className="mt-5 md:mt-10 w-full max-w-[520px]">
            {submitted ? (
              <div
                className="bg-[#fafafa] rounded-2xl p-6 border border-black/10"
                data-testid="hero-success"
              >
                <p className="text-black text-sm font-bold">
                  You're #{submittedRank ?? waitlistCount} on the list. EDITH
                  will find you when it's time.
                </p>
              </div>
            ) : (
              <div>
                <form onSubmit={handleSubmit} data-testid="hero-waitlist-form">
                  <div className="flex flex-col sm:flex-row gap-3 sm:gap-0 sm:items-center sm:bg-[#fafafa] sm:border sm:border-black/10 sm:rounded-xl sm:overflow-hidden sm:focus-within:border-black/30 sm:transition-all sm:duration-200">
                    <input
                      type="email"
                      placeholder="your@email.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      data-testid="hero-email-input"
                      className="w-full min-w-0 bg-[#fafafa] border border-black/10 rounded-xl px-5 py-4 text-base text-black placeholder:text-black/30 focus:outline-none focus:border-black/30 sm:border-none sm:rounded-none sm:bg-transparent sm:flex-1 sm:focus:border-transparent transition-colors"
                    />
                    <button
                      type="submit"
                      disabled={loading}
                      data-testid="hero-submit-btn"
                      className="w-full sm:w-auto bg-[#111] text-white px-6 py-4 sm:py-3.5 sm:mr-1.5 rounded-xl sm:rounded-[10px] font-bold text-sm uppercase tracking-wider hover:bg-black/80 transition-all duration-200 disabled:opacity-50 flex items-center justify-center gap-2 whitespace-nowrap"
                    >
                      {loading ? (
                        "Joining..."
                      ) : (
                        <>
                          Join the Waitlist <ArrowRight size={14} strokeWidth={2.5} />
                        </>
                      )}
                    </button>
                  </div>
                  {(localError || message) && !submitted && (
                    <p
                      role="alert"
                      className="mt-2 text-sm text-red-600 font-medium"
                      data-testid="hero-message"
                    >
                      {localError || message}
                    </p>
                  )}
                </form>

                {/* ── Progress bar ── */}
                <div className="mt-5 md:mt-4">
                  <div className="w-full h-[3px] bg-[#e4e4e7] rounded-full overflow-hidden">
                    <div
                      className="h-full bg-[#111] rounded-full transition-all duration-700 ease-out"
                      style={{ width: `${progressPct}%` }}
                    />
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-[11px] md:text-xs text-black/40">
                      <span className="font-bold text-black/60">{claimed}</span>{" "}
                      of {TOTAL_SPOTS} founding spots claimed
                    </span>
                    <span className="text-[11px] md:text-xs font-bold text-black/70">
                      {remaining} left
                    </span>
                  </div>
                </div>

                {/* ── Founding member note ── */}
                <p className="mt-3 text-[11px] md:text-xs text-black/40 text-center">
                  <span className="font-bold text-black/60">
                    Lifetime discount
                  </span>{" "}
                  for founding members
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
